import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Button,
  InputAdornment,
  IconButton,
  TableSortLabel,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import codexService from '../services/codexService';
import codexTypeService from '../services/codexTypeService';
import { STATUS_CONFIG, getStatusConfig } from '../config/statusConfig';
import RoleService from '../services/RoleService';
import useListState from '../hooks/useListState';
import { API_URL, API_BASE_PATH } from '../services/apiConfig';
import keycloak from '../config/keycloak';

const CodexList = () => {
  const navigate = useNavigate();

  // State for data
  const [codexes, setCodexes] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [codexTypes, setCodexTypes] = useState([]);
  const [failedThumbnails, setFailedThumbnails] = useState(new Set());
  const [thumbnails, setThumbnails] = useState({});
  const [thumbnailReloadTrigger, setThumbnailReloadTrigger] = useState(0);
  
  // URL-synchronized state for filters, pagination, and sorting
  const {
    searchQuery,
    page,
    rowsPerPage,
    orderBy,
    order,
    statusFilter,
    handleSearchChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleSortChange,
    setStatusFilter,
    setPage,
    clearSearch,
    buildFilters
  } = useListState({
    searchParam: 'search',
    statusParam: 'status',
    pageParam: 'page',
    sizeParam: 'size',
    sortByParam: 'sortBy',
    sortOrderParam: 'sortOrder',
    defaults: {
      search: '',
      status: '',
      page: 0,
      size: 10,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    },
    options: {
      searchDebounceMs: 500
    }
  });

  // Add type filter as separate URL param (not included in useListState by default)
  const [typeFilter, setTypeFilter] = useState('');

  // Ref to avoid stale closure
  const setPageRef = useRef(setPage);
  setPageRef.current = setPage;

  // Fetch codex types on component mount
  useEffect(() => {
    const fetchCodexTypes = async () => {
      try {
        const types = await codexTypeService.getAll();
        setCodexTypes(types);
      } catch (error) {
        console.error('Error fetching codex types:', error);
        setError('Failed to load codex types');
      }
    };
    fetchCodexTypes();
  }, []);

  // Enhanced search change handler with loading state
  const handleSearchChangeWithLoading = useCallback((event) => {
    if (!event || !event.target) return;
    
    setIsSearching(true);
    handleSearchChange(event);
    
    // Reset the searching state after a delay
    setTimeout(() => {
      setIsSearching(false);
    }, 600); // Slightly longer than the debounce
  }, [handleSearchChange]);

  // Handle type filter change
  const handleTypeChange = useCallback((event) => {
    if (!event || !event.target) return;
    
    const value = event.target.value;
    setTypeFilter(value);
    
    // Reset to first page when changing filters
    if (page !== 0) {
      setPage(0);
    }
  }, [page, setPage]);

  // Handle status filter change with page reset
  const handleStatusFilterChange = useCallback((event) => {
    if (!event || !event.target) return;
    
    setStatusFilter(event.target.value);
    
    // Reset to first page when changing filters
    if (page !== 0) {
      setPage(0);
    }
  }, [page, setPage, setStatusFilter]);

  // Simple refetch function for manual refreshes
  const fetchCodexes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get base filters from the useListState hook
      const baseFilters = buildFilters();
      
      // Add type filter which is not part of useListState
      const filters = {
        ...baseFilters,
        codexTypeId: typeFilter || undefined
      };
      
      const response = await codexService.getAll(filters);

      setCodexes(response.content || []);
      setTotalElements(response.totalElements || 0);
    } catch (error) {
      console.error("Error fetching codexes:", error);
      setError("Failed to load codexes");
      setCodexes([]);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  // Clear search with reset of isSearching state
  const handleClearSearch = useCallback(() => {
    setIsSearching(false);
    clearSearch();
    
    // If on page other than first, reset to first page
    if (page !== 0) {
      setPage(0);
    }
  }, [clearSearch, page, setPage]);

  // Load data when URL-synchronized state or type filter changes
  useEffect(() => {
    // Track if the component is mounted
    let isMounted = true;
    
    const loadData = async () => {
      if (!isMounted) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Get base filters from the useListState hook
        const baseFilters = buildFilters();
        
        // Add type filter which is not part of useListState
        const filters = {
          ...baseFilters,
          codexTypeId: typeFilter || undefined
        };
        
        const response = await codexService.getAll(filters);
        
        // Check if component is still mounted before updating state
        if (!isMounted) return;

        setCodexes(response.content || []);
        setTotalElements(response.totalElements || 0);
      } catch (error) {
        // Check if component is still mounted before updating state
        if (!isMounted) return;

        console.error("Error fetching codexes:", error);
        setError("Failed to load codexes");
        setCodexes([]);
      } finally {
        // Check if component is still mounted before updating state
        if (isMounted) {
          setLoading(false);
          setIsSearching(false);
        }
      }
    };

    loadData();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [page, rowsPerPage, orderBy, order, statusFilter, searchQuery, typeFilter, buildFilters]);

  // Reload all thumbnails when codexes change or when trigger is updated
  useEffect(() => {
    if (codexes.length === 0) {
      return;
    }
    
    // Clear ALL thumbnails to force reload
    setThumbnails({});
    setFailedThumbnails(new Set());
    
    // Reload all thumbnails (no caching)
    codexes.forEach(codex => {
      loadThumbnail(codex);
    });
  }, [codexes, loadThumbnail, thumbnailReloadTrigger]);

  // Reload thumbnails when window gains focus (e.g., after uploading a file in a modal)
  useEffect(() => {
    const handleFocus = () => {
      setThumbnailReloadTrigger(prev => prev + 1);
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Periodically check for thumbnail updates when page is visible
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible' && codexes.length > 0) {
        setThumbnailReloadTrigger(prev => prev + 1);
      }
    }, 10000); // Check every 10 seconds
    
    return () => clearInterval(intervalId);
  }, [codexes.length]);

  const shouldShowAddButton = RoleService.hasCodexInsertRole();

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return dayjs(dateString).format('DD/MM/YYYY HH:mm:ss');
  };

  // Load thumbnail for a specific codex (always reload)
  const loadThumbnail = useCallback((codex) => {
    const codexId = codex.id;
    
    // Always reload thumbnail - no caching
    codexService.getThumbnail(codexId, codex.updatedAt)
      .then(response => {
        // response is null if thumbnail doesn't exist (404)
        if (response && response.thumbnail) {
          setThumbnails(current => ({ ...current, [codexId]: response.thumbnail }));
        } else {
          // No thumbnail available - silently mark as failed
          setFailedThumbnails(current => new Set(current).add(codexId));
        }
      })
      .catch(error => {
        // Only log real errors (not 404s which return null above)
        console.error(`Error loading thumbnail for codex ${codexId}:`, error);
        setFailedThumbnails(current => new Set(current).add(codexId));
      });
  }, []);

  // Handle thumbnail load error
  const handleThumbnailError = useCallback((codexId) => {
    setFailedThumbnails(prev => new Set(prev).add(codexId));
  }, []);

  if (loading && !isSearching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Lista Codex</Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Cerca per codice o descrizione..."
                value={searchQuery}
                onChange={handleSearchChangeWithLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {isSearching ? (
                        <CircularProgress size={20} />
                      ) : searchQuery ? (
                        <IconButton size="small" onClick={handleClearSearch}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Stato</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Stato"
                    onChange={handleStatusFilterChange}
                    renderValue={(selected) => {
                      if (!selected) return "Tutti";
                      const statusConfig = getStatusConfig('CODEX', selected);
                      return (
                        <Chip 
                          label={statusConfig.label}
                          color={statusConfig.color}
                          size="small"
                        />
                      );
                    }}
                  >
                    <MenuItem value="">Tutti</MenuItem>
                    {Object.entries(STATUS_CONFIG.CODEX).map(([status, config]) => (
                      <MenuItem key={status} value={status}>
                        <Chip 
                          label={config.label} 
                          size="small" 
                          color={config.color} 
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={handleTypeChange}
                    label="Tipo"
                  >
                    <MenuItem value="">Tutti</MenuItem>
                    {codexTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                {shouldShowAddButton && (
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/new-codex')}
                  >
                    Aggiungi
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell align="center" sx={{ width: 80 }}>
                  Thumbnail
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'code'}
                    direction={orderBy === 'code' ? order : 'asc'}
                    onClick={() => handleSortChange('code')}
                  >
                    Codice
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'description'}
                    direction={orderBy === 'description' ? order : 'asc'}
                    onClick={() => handleSortChange('description')}
                  >
                    Descrizione
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'codexTypeCode'}
                    direction={orderBy === 'codexTypeCode' ? order : 'asc'}
                    onClick={() => handleSortChange('codexTypeCode')}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleSortChange('status')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'createdAt'}
                    direction={orderBy === 'createdAt' ? order : 'asc'}
                    onClick={() => handleSortChange('createdAt')}
                  >
                    Data Creazione
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'updatedAt'}
                    direction={orderBy === 'updatedAt' ? order : 'asc'}
                    onClick={() => handleSortChange('updatedAt')}
                  >
                    Data Ultima Modifica
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {codexes.length > 0 ? (
                codexes.map((codex) => {
                  const statusConfig = getStatusConfig('CODEX', codex.status);
                  return (
                    <TableRow
                      key={codex.id}
                      hover
                      onClick={() => navigate(`/codex/${codex.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        {thumbnails[codex.id] && !failedThumbnails.has(codex.id) ? (
                          <Avatar
                            variant="rounded"
                            sx={{ width: 60, height: 60, margin: 'auto' }}
                          >
                            <img
                              src={thumbnails[codex.id]}
                              alt="thumbnail"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={() => handleThumbnailError(codex.id)}
                            />
                          </Avatar>
                        ) : (
                          <Avatar
                            variant="rounded"
                            sx={{ width: 60, height: 60, margin: 'auto' }}
                          >
                            <DescriptionIcon />
                          </Avatar>
                        )}
                      </TableCell>
                      <TableCell>{codex.code || '-'}</TableCell>
                      <TableCell>{codex.description || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={codex.codexTypeDescription || 'Missing description'}>
                          <Chip 
                            label={codex.codexTypeCode || '-'} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={statusConfig.label}
                          color={statusConfig.color}
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>{formatDate(codex.createdAt)}</TableCell>
                      <TableCell>{formatDate(codex.updatedAt)}</TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No codexes found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>
    </Box>
  );
};

export default CodexList;